import {
  Component,
  ComponentRef,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject, fromEvent } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  takeUntil,
} from 'rxjs/operators';
import { GoogleAnalyticsService } from 'google-analytics.service';
import { JoyrideService } from 'ngx-joyride';
import { NbDialogService, NbToastrService, NbSidebarService } from '@nebular/theme';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortable, Sort } from '@angular/material/sort';

import {
  PatientData,
  PatientFilter,
  PatientWithChatThread,
} from 'app/@core/interfaces/common/CalorieFriend/patients';
import { User, UserData } from 'app/@core/interfaces/common/users';
import {
  SubscriptionPlan,
  SubscriptionPlanData,
} from 'app/@core/interfaces/common/CalorieFriend/subscriptionPlan';
import {
  TutorialData,
  TutorialIDs,
} from 'app/@core/interfaces/common/CalorieFriend/tutorial';
import { FacebookPixelService } from 'app/@theme/services/FacebookPixelService';
import { ConfirmMessageDialogComponent } from 'app/@components/confirm-message-dialog/confirm-message-dialog.component';
import { UserStore } from 'app/@core/stores/user.store';

import { UpgradePlanComponent } from './UpgradePlan/upgrade-plan.component';
import { InitialUserInfoComponent } from '../InitialUserInfo/initial-user-info.component';
import { PremiumModalComponent } from '../premium-modal/premium-modal.component';
import { NativeAppService } from 'app/@core/backend/common/services/native-app.service';
import { TheadTitlesRowComponent } from 'ng2-smart-table/lib/components/thead/rows/thead-titles-row.component';
import { ChatThreadService } from 'app/@chat/services/chat-thread.service';
import { ChatService } from 'app/@chat/chat.service';
import { GridData } from 'app/@core/interfaces/common/grid-data';

@Component({
  selector: 'app-patients',
  templateUrl: './patients.component.html',
  styleUrls: ['./patients.component.scss'],
})
export class PatientsComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator, { static: true }) paginator?: MatPaginator;
  @ViewChild(MatSort, { static: true }) sortor?: MatSort;
  @ViewChild('search', { static: true }) searchInput?: ElementRef;

  static tutorialStarted: boolean = false;

  readonly TutorialIDs = TutorialIDs;
  readonly displayedColumns = ['name', 'plansCount', 'compliance', 'createdDate', 'actions'];
  totalCount: number = 0;
  dataList: PatientWithChatThread[] = [];
  user: User;
  shouldManageSubscription: boolean = false;
  isUserEmailVerified: boolean = false;
  patientsCountInfo: string = '';
  isAddNewTutorial: boolean = false;
  subscriptionPlan: SubscriptionPlan;
  maxPatientCount: number;
  contentLoaded: boolean = false;

  private readonly unsubscribe$ = new Subject<void>();
  private paging: PatientFilter = {
    isArchived: false,
    sortBy: 'createdDate',
    orderBy: 'desc',
  };
  private loaded: boolean = false;

  constructor(
    public readonly joyrideService: JoyrideService,
    private router: Router,
    private patientsService: PatientData,
    private usersService: UserData,
    private userStore: UserStore,
    private subscriptionPlansService: SubscriptionPlanData,
    private dialogService: NbDialogService,
    private toasterService: NbToastrService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private readonly tutorialService: TutorialData,
    protected facebookPixelService: FacebookPixelService,
    private nativeAppService: NativeAppService,
    private chatService: ChatService,
    private chatThreadService: ChatThreadService,
    private sidebarService: NbSidebarService
  ) {}

  ngOnInit(): void {
    this.usersService.getCurrentUser().subscribe((user) => {
      this.userStore.setUser(user);

      if (this.userStore.getUser()) {
        this.checkUserState(this.userStore.getUser());
      }
      else
        this.userStore
          .onUserStateChange()
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe((user) => this.checkUserState(user));

      // every visit - update FB and GA
      // fb pixel
      this.facebookPixelService.load(FacebookPixelService.PageViewEvent);

      // GA
      this.googleAnalyticsService.eventEmitter(FacebookPixelService.PageViewEvent, 'Patients', 'Index');

      // default sort and load data initially
      this.sortor.sort({
        id: this.paging.sortBy,
        start: this.paging.orderBy,
      } as MatSortable);

      // search event
      fromEvent(this.searchInput.nativeElement, 'keyup')
        .pipe(
          takeUntil(this.unsubscribe$),
          filter((x) => x !== this.paging.nameAndEmail),
          debounceTime(250),
          distinctUntilChanged()
        )
        .subscribe(() => {
          this.paging.nameAndEmail = this.searchInput.nativeElement.value;
          this.loadDataList();
        });
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private checkUserState(user: User): void {
    if (!user || this.loaded) return;
    this.loaded = true;

    this.isUserEmailVerified = user.isEmailValid;
    this.user = user;

    // load initial user info
    if (!user.isRegistrationCompleted) {
      setTimeout(() =>
        this.dialogService
          .open(InitialUserInfoComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
          })
          .onClose.subscribe(() => {
            this.user.isRegistrationCompleted = true;
            setTimeout(() => this.loadTutorial(), 1000);
          })
      );
    } else {
      setTimeout(() => this.loadTutorial(), 1000);
    }

    // load current plan
    this.subscriptionPlansService
      .getByCode(this.user.planCode)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((plan) => {
        this.subscriptionPlan = plan;
        this.updatePatientsCountInfo();
      });

    // subscription info
    this.shouldManageSubscription = user?.isAmbasador === false && user?.selectedPlan !== user?.planCode;
  }

  onSortChanged(sort: Sort): void {
    this.paging.sortBy = sort.active;
    this.paging.orderBy = sort.direction;
    this.loadDataList(true);
  }

  loadDataList(pagination: boolean = false): void {
    this.contentLoaded = false;
    console.log('initial');
    if (!pagination && this.paginator) this.paginator.pageIndex = 0;
    this.paging.pageNumber = pagination ? this.paginator?.pageIndex + 1 ?? 1 : 1;
    this.paging.pageSize = this.paginator?.pageSize ?? 10;

    this.patientsService
      .list(this.paging)
      .pipe(takeUntil(this.unsubscribe$))
      .pipe(
        mergeMap((res) => {
          return this.chatService.getThreads().pipe(
            map((threads) => {
              const patientsWithChatThread = res.items.map<PatientWithChatThread>((patient) => {
                const thread = threads.find((th) => th.users[0].id == patient.id);
                return {
                  ...patient,
                  chatThread: thread,
                };
              });

              const gridData: GridData<PatientWithChatThread> = {
                ...res,
                items: patientsWithChatThread,
              };

              return gridData;
            })
          );
        })
      )
      .subscribe((res) => {
        this.totalCount = res.totalCount;
        this.dataList = res.items;
        console.log(res.items);
        this.contentLoaded = true;
      });
  }

  private loadTutorial(): void {
    if (!this.user.isRegistrationCompleted) return;
    if (this.nativeAppService.isFromApp()) return;
    if (PatientsComponent.tutorialStarted) return; //extra cautius, has issue once where tutorial was able to restart when was completed.

    // demo patient tutorial
    this.patientsService
      .getDemoPatient()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((demoPatient) => {
        if (!demoPatient) return;

        const tutorialStates = JSON.parse(this.user.tutorialStates ?? '{}');
        const steps = [];

        // check the tutorial step 1 - demo patient
        if (!tutorialStates[TutorialIDs.DemoPatient]) {
          steps.push(TutorialIDs.DemoPatient);
          steps.push(TutorialIDs.AddNewPatient);
        } else {
          return;
        }

        PatientsComponent.tutorialStarted = true;
        this.joyrideService
          .startTour({
            steps: steps,
            showPrevButton: false,
            showCounter: true,
          })
          .subscribe((step) => {
            if (step.name === TutorialIDs.AddNewPatient) this.isAddNewTutorial = true;
          });
      });
  }

  private updatePatientsCountInfo(): void {
    this.patientsService
      .getActiveCount()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((count) => {
        if (this.subscriptionPlan?.patientsCount > 999 || this.userStore.isUnlimitedTrialActive()) {
          this.patientsCountInfo = '(' + count + ')';
        } else {
          this.patientsCountInfo = '(' + count + '/' + +this.subscriptionPlan?.patientsCount + ')';
        }
      });
  }

  onDelete(id: number): void {
    this.dialogService
      .open(ConfirmMessageDialogComponent, {
        closeOnEsc: true,
        closeOnBackdropClick: true,
        context: { Message: 'Are you sure to delete this patient?' },
      })
      .onClose.subscribe((res) => {
        if (!res) return;
        this.patientsService
          .delete(id)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe(
            () => {
              this.patientsService.setPatientUpdated(id);
              this.toasterService.success('', 'Successfully deleted Client');
              this.loadDataList(true);
              this.updatePatientsCountInfo();
            },
            () => this.toasterService.danger('', 'Problem occurred while delelting Client')
          );
      });
  }

  onArchivedChange(checked: boolean): void {
    this.paging.isArchived = checked;
    this.loadDataList();
  }

  onUnarchive(id: number): void {
    this.patientsService
      .getActiveCount()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((count) => {
        this.patientsCountInfo = '(' + count + '/' + +this.subscriptionPlan?.patientsCount + ')';
        if (!this.canAddNewPatients(count)) {
          this.dialogService.open(UpgradePlanComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
          });
        } else {
          // ok, we can continue
          this.patientsService
            .unarchive(id)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(
              () => {
                this.toasterService.success('', 'Successfully unarchived Client');
                this.patientsService.setPatientUpdated(id);
                this.loadDataList(true);
                this.updatePatientsCountInfo();
              },
              () => this.toasterService.danger('', 'Problem occurred while unarchiving Client')
            );
        }
      });
  }

  tryArchiveClient(id) {
    if (this.userStore.isPayingUser()) {
      this.onArchive(id);
    } else {
      this.patientsService
        .get(id, null)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((patient) => {
          // allow demo client or tutorial client being created.
          if (patient.isDemoPatient || patient.eMail == this.user.email) {
            this.onArchive(id);
          } else {
            this.onShowPremiumDialog();
          }
        });
    }
  }

  onShowPremiumDialog() {
    const ref: ComponentRef<PremiumModalComponent> = this.dialogService.open(PremiumModalComponent, {
      closeOnBackdropClick: false,
      closeOnEsc: false,
      autoFocus: false,
      context: {},
    }).componentRef;
  }

  onArchive(id: number): void {
    this.dialogService
      .open(ConfirmMessageDialogComponent, {
        closeOnEsc: true,
        closeOnBackdropClick: true,
        context: {
          Message:
            'Warning, archiving a patient will disable patient zone and patient assessments. Archived patients can be re-activated later.',
        },
      })
      .onClose.subscribe((res) => {
        if (!res) return;
        this.patientsService
          .archive(id)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe(
            () => {
              this.toasterService.success('', 'Successfully archived Client');
              this.patientsService.setPatientUpdated(id);
              this.loadDataList(true);
              this.updatePatientsCountInfo();
            },
            () => this.toasterService.danger('', 'Problem occurred while archiving Client')
          );
      });
  }

  onAddPatient(): void {
    this.googleAnalyticsService.eventEmitter('add_patient', 'Patients', 'Add');

    if (this.joyrideService.isTourInProgress()) {
      // close the tutorial
      this.joyrideService.closeTour();

      // save the tutorial position
      this.tutorialService.save(TutorialIDs.AddNewPatient).subscribe(() => {});

      // navigate to new client page
      this.router.navigate(['/pages/CalorieFriend/NewPatient/FromTutorial']);
      return;
    }

    this.patientsService
      .getActiveCount()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((count) => {
        this.patientsCountInfo = '(' + count + '/' + +this.subscriptionPlan?.patientsCount + ')';
        if (!this.canAddNewPatients(count)) {
          this.dialogService.open(UpgradePlanComponent, {
            closeOnEsc: false,
            closeOnBackdropClick: false,
          });
        } else {
          this.router.navigate(['/pages/CalorieFriend/NewPatient/']);
        }
      });
  }

  onResendConfirmation(): void {
    this.usersService.submitVerificationEmail(this.user.email).subscribe((res) => {
      if (res) this.toasterService.success('', 'Confirmation email has been sent.');
      else this.toasterService.danger('', 'Problem occurred while sending confirmation email.');
    });
  }

  onCloseEmailWarning(): void {
    this.isUserEmailVerified = true;
  }

  onClosePaymentWarning(): void {
    this.shouldManageSubscription = false;
  }

  get showNotificationPanel(): boolean {
    if (this.shouldManageSubscription) return true;
    if (this.user && this.user.isRegistrationCompleted) return false;
    if (this.isAddNewTutorial) false;
    if (this.isUserEmailVerified) return true;
    return false;
  }

  private canAddNewPatients(count: number) {
    if (this.subscriptionPlan?.patientsCount > 0 && count >= +this.subscriptionPlan?.patientsCount) {
      return false;
    }
    return true;
  }

  openChatWindow(rowId) {
    const thread = this.dataList.find((item) => item.id == rowId)?.chatThread;
    this.chatThreadService.currentThread = thread;
    this.sidebarService.expand('right');
  }
  
  readonly gaugeLabel = (value) => `${Math.round(value * 10) / 10}%`;
}
